import { useEffect, useMemo, useState } from "react";
import {
  TextField,
  FormControl,
  MenuItem,
  Accordion,
  AccordionSummary,
  IconButton,
  Select,
  Typography,
} from "@mui/material";
import PublishIcon from "@mui/icons-material/Publish";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { NavigateBefore } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import AppDialog from "../../../../components/AppDialog/AppDialog";
import CustomButton from "../../../../components/buttons/CustomButton";
import LanguageTabs from "../../../../components/LanguageTab/LanguageTabs";
import DropDownCondition from "../../../../components/buttons/DropDownCondition";
import DropDownOperator from "../../../../components/buttons/DropDownOperator";
import DropDownAction from "../../../../components/buttons/DropDownAction";
import { useAuthRoles } from "../../../../hooks/useAuthRoles";
import { useWorkerApiHelper } from "../../../../hooks/useWorkerApiHelper";
import { availableLanguages } from "../../../../i18n";

import { usePostPolicy } from "../../../../hooks/policies/usePostPolicy";
import { usePutPolicy } from "../../../../hooks/policies/usePutPolicy";
import { useGetPolicyById } from "../../../../hooks/policies/useGetPolicyById";
import { useGetParticipantByParticipantId } from "../../../../hooks/participants/useGetParticipantByParticipantId";
import { formatDate, getControlledList } from "../../../../utils/constants";
import {
  filterPolicyActionOptions,
  filterPolicyConditionOptions,
  filterPolicyRecipientOperatorOptions,
  formatPolicyDatetimeValue,
  isPolicyDatetimeCondition,
  isPolicyRecipientCondition,
  parsePolicyDatetimeForInput,
} from "../../../../utils/policies";
import { getParticipantDisplayName } from "../../../../utils/participants";
import { getParticipantIdFromToken } from "../../../DataOfferings/Crud/offeringFormUtils";
import { extractIdFromIri } from "../../../../utils/extractFromIri";
import "./CreatePolicy.css";
import { useGetParticipantsOld } from "../../../../hooks/participants/useGetParticipantsOld";

const EMPTY_CONSTRAINT = () => ({
  left_operand: "",
  operator: "",
  right_operand: "",
});

const omitEmptyArrays = (obj) => {
  const out = { ...obj };
  Object.keys(out).forEach((k) => {
    if (Array.isArray(out[k]) && out[k].length === 0) {
      delete out[k];
    }
  });
  return out;
};

const EMPTY_RULE_ITEM = () => ({
  action: "",
  constraints: [EMPTY_CONSTRAINT()],
});

const EMPTY_POLICY_FORM = (lang) => ({
  title: [{ language: lang, value: "" }],
  description: [{ language: lang, value: "" }],
  permissions: [],
  obligations: [],
  prohibitions: [],
});

const bucketKeyFromRule = (rule) => {
  if (rule === "permission") return "permissions";
  if (rule === "obligation") return "obligations";
  if (rule === "prohibition") return "prohibitions";
  return null;
};

const iriToLabel = (items) => {
  const map = new Map();
  (items || []).forEach((it) => {
    const iri = it?.concept_iri;
    const label = it?.label;
    if (iri && label) map.set(iri, label);
  });
  return map;
};

const labelFor = (map, iri) => {
  if (!iri) return "";
  return map.get(iri) || iri;
};

const safePolicyToForm = (policy, lang) => {
  return {
    title:
      Array.isArray(policy?.title) && policy.title.length
        ? policy.title
        : [{ language: lang, value: "" }],
    description:
      Array.isArray(policy?.description) && policy.description.length
        ? policy.description
        : [{ language: lang, value: "" }],
    permissions: Array.isArray(policy?.permissions) ? policy.permissions : [],
    obligations: Array.isArray(policy?.obligations) ? policy.obligations : [],
    prohibitions: Array.isArray(policy?.prohibitions)
      ? policy.prohibitions
      : [],
  };
};

export default function CreatePolicy({
  open,
  onClose,
  policyId = null,
  loading = false,
  onSuccess,
}) {
  const { t, i18n } = useTranslation();
  const { callApiWithWorker } = useWorkerApiHelper();
  const { decoded } = useAuthRoles();
  const participantIdFromToken = getParticipantIdFromToken(decoded);

  const [controlledVocabularies, setControlledVocabularies] = useState({
    constraint_left_operand: [],
    constraint_operator: [],
    rule_actions: [],
  });

  const actionLabelMap = iriToLabel(controlledVocabularies.rule_actions);
  const conditionLabelMap = iriToLabel(
    controlledVocabularies.constraint_left_operand,
  );
  const operatorLabelMap = iriToLabel(
    controlledVocabularies.constraint_operator,
  );

  useEffect(() => {
    let mounted = true;

    async function fetchVocabularies() {
      const [constraint_left_operand, constraint_operator, rule_actions] =
        await Promise.all([
          getControlledList(callApiWithWorker, "constraint_left_operand", {
            lang: i18n.language,
          }),
          getControlledList(callApiWithWorker, "constraint_operator", {
            lang: i18n.language,
          }),
          getControlledList(callApiWithWorker, "rule_actions", {
            lang: i18n.language,
          }),
        ]);

      if (!mounted) return;

      setControlledVocabularies({
        constraint_left_operand: filterPolicyConditionOptions(
          constraint_left_operand,
        ),
        constraint_operator,
        rule_actions: filterPolicyActionOptions(rule_actions),
      });
    }

    fetchVocabularies();

    return () => {
      mounted = false;
    };
  }, [i18n.language]);

  const isEdit = Boolean(policyId);

  const { data: policy, isLoading: isGetting } = useGetPolicyById({
    callApiWithWorker,
    id: policyId,
    language: i18n.language,
    enabled: open && isEdit,
  });

  const { data: assignerParticipant, isLoading: isGettingAssigner } =
    useGetParticipantByParticipantId({
      callApiWithWorker,
      participantId: participantIdFromToken,
      enabled: open,
    });

  const { mutateAsync: createPolicy, isPending: isCreating } = usePostPolicy({
    callApiWithWorker,
    t,
  });

  const { mutateAsync: updatePolicy, isPending: isUpdating } = usePutPolicy({
    callApiWithWorker,
    t,
  });

  const busy =
    loading || isGetting || isGettingAssigner || isCreating || isUpdating;

  const [policyForm, setPolicyForm] = useState(() =>
    EMPTY_POLICY_FORM(i18n.language),
  );
  const [expanded, setExpanded] = useState(null);
  const [baseLang] = useState(() => i18n.language);
  const [activeLang, setActiveLang] = useState(i18n.language);
  const [participantMenuWidth, setParticipantMenuWidth] = useState(null);
  const { data: participantsResponse, isLoading: isParticipantsLoading } =
    useGetParticipantsOld({
      callApiWithWorker,
      lang: i18n.language,
      pageIndex: 1,
      pageSize: 1000,
    });

  const participantOptions = useMemo(() => {
    const content = participantsResponse?.content ?? [];

    return content
      .map((participant) => {
        const participantId =
          participant?.id ?? participant?.dsbParticipantId ?? "";
        const participantName =
          getParticipantDisplayName(participant, i18n.language) ||
          participant?.legal_name ||
          participantId;

        return {
          value: extractIdFromIri(participantId),
          label: participantName,
        };
      })
      .filter((participant) => participant.value)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [i18n.language, participantsResponse]);

  const participantLookup = useMemo(
    () =>
      participantOptions.reduce((acc, participant) => {
        acc[participant.value] = participant;
        return acc;
      }, {}),
    [participantOptions],
  );

  useEffect(() => {
    if (!open) return;

    if (!isEdit) {
      setPolicyForm(EMPTY_POLICY_FORM(i18n.language));
      setExpanded(null);
      setActiveLang(baseLang);
    }
  }, [open, isEdit, i18n.language, baseLang]);

  useEffect(() => {
    if (!open || !isEdit || !policy) return;
    setPolicyForm(safePolicyToForm(policy, i18n.language));
    setExpanded(null);
    const langsPresent = Array.from(
      new Set(
        [
          ...(Array.isArray(policy?.title) ? policy.title : []),
          ...(Array.isArray(policy?.description) ? policy.description : []),
        ]
          .map((item) => item?.language)
          .filter(Boolean),
      ),
    );
    setActiveLang(
      langsPresent.includes(baseLang) ? baseLang : langsPresent[0] || baseLang,
    );
  }, [open, isEdit, policy, i18n.language, baseLang]);

  const usedLangs = Array.from(
    new Set(
      [
        ...(Array.isArray(policyForm.title) ? policyForm.title : []),
        ...(Array.isArray(policyForm.description)
          ? policyForm.description
          : []),
      ]
        .map((item) => item?.language)
        .filter(Boolean),
    ),
  );
  const effectiveUsedLangs = usedLangs.length > 0 ? usedLangs : [baseLang];

  const availableLangs = availableLanguages.filter(
    (lang) => !effectiveUsedLangs.includes(lang.code),
  );

  const activeTitleValue =
    policyForm.title?.find((item) => item.language === activeLang)?.value ?? "";
  const activeDescriptionValue =
    policyForm.description?.find((item) => item.language === activeLang)
      ?.value ?? "";

  const setTitleValue = (val) => {
    setPolicyForm((prev) => {
      const nextTitle = Array.isArray(prev.title) ? [...prev.title] : [];
      const index = nextTitle.findIndex((item) => item.language === activeLang);

      if (index >= 0) {
        nextTitle[index] = { ...nextTitle[index], value: val };
      } else {
        nextTitle.push({ language: activeLang, value: val });
      }

      return {
        ...prev,
        title: nextTitle,
      };
    });
  };

  const setDescriptionValue = (val) => {
    setPolicyForm((prev) => {
      const nextDescription = Array.isArray(prev.description)
        ? [...prev.description]
        : [];
      const index = nextDescription.findIndex(
        (item) => item.language === activeLang,
      );

      if (index >= 0) {
        nextDescription[index] = { ...nextDescription[index], value: val };
      } else {
        nextDescription.push({ language: activeLang, value: val });
      }

      return {
        ...prev,
        description: nextDescription,
      };
    });
  };

  const addTitleLanguage = (lang) => {
    const code = typeof lang === "string" ? lang : lang?.code;
    if (!code || effectiveUsedLangs.includes(code) || busy) return;

    setPolicyForm((prev) => ({
      ...prev,
      title: [...(prev.title || []), { language: code, value: "" }],
      description: [...(prev.description || []), { language: code, value: "" }],
    }));
    setActiveLang(code);
  };

  const removeTitleLanguage = (lang) => {
    const code = typeof lang === "string" ? lang : lang?.code;
    if (!code || effectiveUsedLangs.length <= 1 || busy) return;

    setPolicyForm((prev) => ({
      ...prev,
      title: (prev.title || []).filter((item) => item.language !== code),
      description: (prev.description || []).filter(
        (item) => item.language !== code,
      ),
    }));

    if (activeLang === code) {
      const nextLang =
        effectiveUsedLangs.find((item) => item !== code) || baseLang;
      setActiveLang(nextLang);
    }
  };

  const addRuleItem = (rule) => {
    const bucket = bucketKeyFromRule(rule);
    if (!bucket || busy) return;

    setPolicyForm((prev) => {
      const nextArr = [...prev[bucket], EMPTY_RULE_ITEM()];
      setExpanded(`${bucket}-${nextArr.length - 1}`);
      return { ...prev, [bucket]: nextArr };
    });
  };

  const updateRuleItem = (bucket, idx, patch) => {
    setPolicyForm((prev) => {
      const arr = [...(prev[bucket] || [])];
      const existing = arr[idx] || EMPTY_RULE_ITEM();
      arr[idx] = { ...existing, ...patch };
      return { ...prev, [bucket]: arr };
    });
  };

  const updateConstraint0 = (bucket, idx, patch) => {
    setPolicyForm((prev) => {
      const arr = [...(prev[bucket] || [])];
      const item = { ...(arr[idx] || EMPTY_RULE_ITEM()) };
      const c0 = { ...(item.constraints?.[0] || EMPTY_CONSTRAINT()) };

      item.constraints = [{ ...c0, ...patch }];
      arr[idx] = item;
      return { ...prev, [bucket]: arr };
    });
  };

  const removeRuleItem = (bucket, idx) => {
    if (busy) return;
    setPolicyForm((prev) => ({
      ...prev,
      [bucket]: (prev[bucket] || []).filter((_, i) => i !== idx),
    }));
    setExpanded((prev) => (prev === `${bucket}-${idx}` ? null : prev));
  };

  const renderBucket = (bucket, bucketTitleKey) => {
    const items = policyForm[bucket] || [];
    if (!items.length) return null;

    return (
      <div className="policyDrafts">
        <Typography variant="subtitle1" className="policyDraftsTitle">
          {t(bucketTitleKey)}
        </Typography>

        {items.map((item, idx) => {
          const accId = `${bucket}-${idx}`;
          const c0 = item.constraints?.[0] || {};
          const isRecipientCondition = isPolicyRecipientCondition(
            c0.left_operand,
            controlledVocabularies.constraint_left_operand,
          );
          const isDatetimeCondition = isPolicyDatetimeCondition(
            c0.left_operand,
            controlledVocabularies.constraint_left_operand,
          );
          const operatorItems = isRecipientCondition
            ? filterPolicyRecipientOperatorOptions(
                controlledVocabularies.constraint_operator,
              )
            : controlledVocabularies.constraint_operator;
          const rightOperandLabel = isRecipientCondition
            ? participantLookup[c0.right_operand]?.label || c0.right_operand
            : isDatetimeCondition
              ? formatDate(c0.right_operand, i18n.language || "es-ES") ||
                c0.right_operand
              : c0.right_operand;

          return (
            <Accordion
              key={accId}
              expanded={expanded === accId}
              onChange={() => setExpanded((p) => (p === accId ? null : accId))}
              className="policyAccordion"
            >
              <AccordionSummary className="policyAccordionSummary">
                <div className="policyAccordionSummaryRow">
                  <div className="policyAccordionMain">
                    <div className="policyMeta policyMetaInline">
                      <span className="policyTag">
                        {item.action
                          ? labelFor(actionLabelMap, item.action)
                          : t("-")}
                      </span>

                      <span className="policyTag">
                        {c0.left_operand
                          ? labelFor(conditionLabelMap, c0.left_operand)
                          : t("-")}
                      </span>

                      <span className="policyTag">
                        {c0.operator
                          ? labelFor(operatorLabelMap, c0.operator)
                          : t("-")}
                      </span>
                      <span className="policyTag policyTagDetails">
                        {rightOperandLabel || t("-")}
                      </span>
                    </div>
                  </div>

                  <IconButton
                    component="span"
                    size="small"
                    className="policyAccordionDelete"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeRuleItem(bucket, idx);
                    }}
                    disabled={busy}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </div>
              </AccordionSummary>

              <div className="createPolicyGrid" style={{ padding: 16 }}>
                <div>
                  <Typography className="ss-label">
                    {t("privateArea.metadataPolicies.action")}
                  </Typography>

                  <DropDownAction
                    value={item.action || ""}
                    onChange={(conceptIri) =>
                      updateRuleItem(bucket, idx, { action: conceptIri })
                    }
                    items={controlledVocabularies.rule_actions}
                    disabled={busy}
                  />
                </div>

                <div>
                  <Typography className="ss-label">
                    {t("privateArea.metadataPolicies.condition")}
                  </Typography>

                  <DropDownCondition
                    value={c0.left_operand || ""}
                    onChange={(conceptIri) =>
                      updateConstraint0(bucket, idx, {
                        left_operand: conceptIri,
                      })
                    }
                    items={controlledVocabularies.constraint_left_operand}
                    disabled={busy}
                  />
                </div>

                <div>
                  <Typography className="ss-label">
                    {t("privateArea.metadataPolicies.operator")}
                  </Typography>

                  <DropDownOperator
                    value={c0.operator || ""}
                    onChange={(conceptIri) =>
                      updateConstraint0(bucket, idx, { operator: conceptIri })
                    }
                    items={operatorItems}
                    disabled={busy}
                  />
                </div>
                <div>
                  <Typography className="ss-label">
                    {t("wizard.steps.policy.details")}
                  </Typography>

                  {isRecipientCondition ? (
                    <FormControl fullWidth>
                      <Select
                        value={c0.right_operand || ""}
                        onOpen={(e) =>
                          setParticipantMenuWidth(
                            e?.currentTarget?.clientWidth || null,
                          )
                        }
                        onChange={(e) =>
                          updateConstraint0(bucket, idx, {
                            right_operand: e.target.value,
                          })
                        }
                        disabled={busy || isParticipantsLoading}
                        renderValue={(selected) =>
                          participantLookup[selected]?.label || selected
                        }
                        sx={{
                          minWidth: 0,
                          "& .MuiSelect-select": {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          },
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              width: participantMenuWidth || undefined,
                              maxWidth: participantMenuWidth || undefined,
                              maxHeight: 48 * 6,
                            },
                          },
                        }}
                      >
                        {participantOptions.map((participant) => (
                          <MenuItem
                            key={participant.value}
                            value={participant.value}
                            sx={{
                              whiteSpace: "normal",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {participant.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      type={isDatetimeCondition ? "date" : "text"}
                      value={
                        isDatetimeCondition
                          ? parsePolicyDatetimeForInput(c0.right_operand)
                          : c0.right_operand || ""
                      }
                      onChange={(e) =>
                        updateConstraint0(bucket, idx, {
                          right_operand: isDatetimeCondition
                            ? formatPolicyDatetimeValue(e.target.value)
                            : e.target.value,
                        })
                      }
                      placeholder={t("wizard.steps.policy.details_placeholder")}
                      fullWidth
                      disabled={busy}
                    />
                  )}
                </div>
              </div>
            </Accordion>
          );
        })}
      </div>
    );
  };

  const titleValue = (
    policyForm.title?.find((item) => item.language === baseLang)?.value ||
    policyForm.title?.find((item) => String(item?.value || "").trim())?.value ||
    ""
  ).trim();
  const hasAtLeastOne =
    (policyForm.permissions?.length || 0) +
      (policyForm.obligations?.length || 0) +
      (policyForm.prohibitions?.length || 0) >
    0;
  const assignerId = assignerParticipant?.id || "";

  const canFinalSubmit = Boolean(titleValue && assignerId && !busy);
  const ruleLabel = hasAtLeastOne
    ? t("wizard.steps.policy.addAnotherRule")
    : t("wizard.steps.policy.rule");

  const handleSubmit = async () => {
    if (!canFinalSubmit || busy) return;

    const rawPayload = {
      ...policyForm,
      title: (policyForm.title || []).filter((item) =>
        String(item?.value || "").trim(),
      ),
      description: (policyForm.description || []).filter((item) =>
        String(item?.value || "").trim(),
      ),
      assigner: assignerId,
    };

    const payload = omitEmptyArrays(rawPayload);

    try {
      if (isEdit) {
        await updatePolicy({ id: policyId, payload });
      } else {
        await createPolicy(payload);
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error("Submit failed:", err);
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={
        isEdit
          ? t("wizard.steps.policy.updatePolicy")
          : t("wizard.steps.policy.create")
      }
      maxWidth="md"
      disableBackdropClose={busy}
      disableEscapeClose={busy}
      actions={
        <>
          <CustomButton
            onClick={onClose}
            disabled={busy}
            startIcon={<NavigateBefore />}
          >
            {t("common.cancel")}
          </CustomButton>

          <CustomButton
            variant="contained"
            onClick={handleSubmit}
            disabled={!canFinalSubmit}
            startIcon={<PublishIcon />}
          >
            {isEdit ? t("common.save") : t("common.create")}
          </CustomButton>
        </>
      }
    >
      <LanguageTabs
        usedLangs={usedLangs}
        activeLang={activeLang}
        onChangeActiveLang={setActiveLang}
        onAddLang={addTitleLanguage}
        onRemoveLang={removeTitleLanguage}
        languages={availableLangs}
        addLabel={t("common.addLanguage")}
        disabled={busy}
      />

      <div className="createPolicyGrid">
        <TextField
          label={t("wizard.steps.policy.name")}
          value={activeTitleValue}
          onChange={(e) => setTitleValue(e.target.value)}
          fullWidth
          disabled={busy}
          required
        />

        <TextField
          label={t("wizard.steps.policy.description", "Description")}
          placeholder={t(
            "wizard.steps.policy.description_placeholder",
            "Write the description",
          )}
          value={activeDescriptionValue}
          onChange={(e) => setDescriptionValue(e.target.value)}
          fullWidth
          disabled={busy}
        />

        <div>
          <TextField
            select
            label={ruleLabel}
            value=""
            onChange={(e) => addRuleItem(e.target.value)}
            fullWidth
            disabled={busy}
          >
            <MenuItem value="permission">
              {t("wizard.steps.policy.permission")}
            </MenuItem>
            <MenuItem value="obligation">
              {t("wizard.steps.policy.obligation")}
            </MenuItem>
            <MenuItem value="prohibition">
              {t("wizard.steps.policy.prohibition")}
            </MenuItem>
          </TextField>
        </div>
      </div>

      {renderBucket("permissions", "wizard.steps.policy.permission")}
      {renderBucket("obligations", "wizard.steps.policy.obligation")}
      {renderBucket("prohibitions", "wizard.steps.policy.prohibition")}
    </AppDialog>
  );
}
